import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "components/atoms/Button";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import { formatBandRange, toOption } from "pages/MasterPengobatanStandar/constants";
import { errorMessages } from "services/StandardTreatment";

export const DOSE_BAND_MODAL_ID = "doseBandFormModal";

const LOWER = [
  { label: "≥ (termasuk)", value: true },
  { label: "> (tidak termasuk)", value: false },
];
const UPPER = [
  { label: "< (tidak termasuk)", value: false },
  { label: "≤ (termasuk)", value: true },
];
const DOSE_MODES = [
  { label: "Dosis tepat (dose_amount)", value: "exact" },
  { label: "Rentang dosis (dose_amount_min – max)", value: "range" },
];

const str = (v) => (v === null || v === undefined ? "" : String(Number(v)));
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// Body mengikuti DoseBandCreate/DoseBandUpdate. Hanya SATU bentuk dosis yang dikirim;
// PATCH mengganti ketiga field dosis sekaligus (HANDOFF §6.1), jadi selalu kirim lengkap.
const toBody = (v) => ({
  min_value: num(v.min_value),
  max_value: num(v.max_value),
  min_inclusive: v.min_inclusive,
  max_inclusive: v.max_inclusive,
  dose_amount: v.dose_mode === "exact" ? num(v.dose_amount) : null,
  dose_amount_min: v.dose_mode === "range" ? num(v.dose_amount_min) : null,
  dose_amount_max: v.dose_mode === "range" ? num(v.dose_amount_max) : null,
  dose_unit: v.dose_unit.trim(),
  dose_display: v.dose_display.trim() || null,
});

const DoseBandFormModal = ({ mode, band, regimen, onSubmit }) => {
  const [serverErrors, setServerErrors] = useState([]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      min_value: str(band?.min_value),
      max_value: str(band?.max_value),
      min_inclusive: band?.min_inclusive ?? true,
      max_inclusive: band?.max_inclusive ?? false,
      dose_mode: band && band.dose_amount === null ? "range" : "exact",
      dose_amount: str(band?.dose_amount),
      dose_amount_min: str(band?.dose_amount_min),
      dose_amount_max: str(band?.dose_amount_max),
      dose_unit: band?.dose_unit || "tablet",
      dose_display: band?.dose_display || "",
    },
    validationSchema: Yup.object({
      dose_unit: Yup.string().trim().max(30).required("Satuan dosis wajib diisi"),
      dose_display: Yup.string().max(150),
      dose_amount: Yup.string().when("dose_mode", { is: "exact", then: (s) => s.required("Isi dosis") }),
      dose_amount_min: Yup.string().when("dose_mode", { is: "range", then: (s) => s.required("Isi dosis minimum") }),
      dose_amount_max: Yup.string().when("dose_mode", { is: "range", then: (s) => s.required("Isi dosis maksimum") }),
      max_value: Yup.string().test("order", "Batas atas harus lebih besar dari batas bawah", function (max) {
        // Formik mengubah "" menjadi undefined sebelum validasi; kosong = tanpa batas.
        const { min_value } = this.parent;
        const blank = (x) => x === undefined || x === "";
        return blank(max) || blank(min_value) || Number(max) > Number(min_value);
      }),
    }),
    onSubmit: (values) => {
      setServerErrors([]);
      return onSubmit(toBody(values)).catch((err) => setServerErrors(errorMessages(err)));
    },
  });

  const v = formik.values;
  const errorOf = (key) => formik.touched[key] && formik.errors[key];
  const field = (key, props) => (
    <InputField
      type="decimal"
      {...formik.getFieldProps(key)}
      error={errorOf(key)}
      errorMessage={formik.errors[key]}
      {...props}
    />
  );
  const select = (key, options, label) => (
    <InputSelect
      label={label}
      isClearable={false}
      data={options}
      value={toOption(options, v[key])}
      onChange={(sel) => formik.setFieldValue(key, sel?.value)}
    />
  );
  const preview = formatBandRange({ ...toBody(v) });
  const isOverlap = serverErrors.some((m) => m.includes("tumpang tindih"));

  return (
    <div className="modal fade md-modal" id={DOSE_BAND_MODAL_ID} tabIndex={-1} aria-labelledby={`${DOSE_BAND_MODAL_ID}Label`} aria-hidden="true">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${DOSE_BAND_MODAL_ID}Label`}>
              {mode === "edit" ? "Edit" : "Tambah"} Dosis Standar{" "}
              <span className="mps-muted">· {regimen?.code}</span>
            </h5>
            <button type="button" className="close" data-dismiss="modal" aria-label="Tutup">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className="modal-body">
              {serverErrors.length > 0 && (
                <div className="mps-alert is-error" role="alert">
                  <div>
                    <p className="mps-alert-title">
                      {isOverlap ? "Dosis standar tumpang tindih dengan dosis lain" : "Gagal menyimpan"}
                    </p>
                    <ul className="mps-alert-list">
                      {serverErrors.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <h6 className="mps-subsection-title mt-0">Rentang berat (kg)</h6>
              <div className="row">
                <div className="col-6 col-md-3 md-field">{select("min_inclusive", LOWER, "Batas bawah")}</div>
                <div className="col-6 col-md-3 md-field">
                  {field("min_value", { label: " ", placeholder: "Kosong = tanpa batas" })}
                </div>
                <div className="col-6 col-md-3 md-field">{select("max_inclusive", UPPER, "Batas atas")}</div>
                <div className="col-6 col-md-3 md-field">
                  {field("max_value", { label: " ", placeholder: "Kosong = tanpa batas" })}
                </div>
              </div>
              <p className="mps-helper mt-0 mb-3">
                Rentang: <strong>{preview}</strong>. Umumnya <code>[min, max)</code>; Kina memakai{" "}
                <code>(min, max]</code>.
              </p>

              <h6 className="mps-subsection-title">Dosis</h6>
              <div className="row">
                <div className="col-md-6 md-field">{select("dose_mode", DOSE_MODES, "Bentuk dosis")}</div>
                {v.dose_mode === "exact" ? (
                  <div className="col-md-3 col-6 md-field">{field("dose_amount", { label: "Dosis", isRequired: true })}</div>
                ) : (
                  <>
                    <div className="col-md-3 col-6 md-field">{field("dose_amount_min", { label: "Min", isRequired: true })}</div>
                    <div className="col-md-3 col-6 md-field">{field("dose_amount_max", { label: "Maks", isRequired: true })}</div>
                  </>
                )}
                <div className={`${v.dose_mode === "exact" ? "col-md-3" : "col-md-6"} col-6 md-field`}>
                  <InputField
                    label="Satuan"
                    isRequired
                    placeholder="tablet / item"
                    {...formik.getFieldProps("dose_unit")}
                    error={errorOf("dose_unit")}
                    errorMessage={formik.errors.dose_unit}
                  />
                </div>
                <div className="col-md-6 md-field">
                  <InputField label="Teks tampilan (opsional)" placeholder="Contoh: 0,5 tablet" {...formik.getFieldProps("dose_display")} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button type="button" variant="secondary" size="normal" data-dismiss="modal">
                Batal
              </Button>
              <Button type="submit" variant="primary" size="normal" isDisabled={formik.isSubmitting}>
                {formik.isSubmitting ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DoseBandFormModal;
